import { MouseEvent, useMemo } from 'react';

import classNames from 'classnames';

import ImageProxy from '@next-image-proxy';

import { useTokenQuery } from '@query';

import { DeserializedAppChainData } from '@api';

import FilledHeart from '@uikit/icons/FilledHeart';
import Loading from '@uikit/icons/Loading';
import OutlinedHeart from '@uikit/icons/OutlinedHeart';

import { multiChain } from '@shared/assets/images';
import NextLink from '@shared/common/components/NextLink';
import ReadMoreText from '@shared/common/components/ReadMoreText';
import { useAuth } from '@shared/common/providers/AuthProvider';
import { useAuthModal } from '@shared/common/providers/GlobalModalsProvider';
import { APP_STATUS, getAppStatus } from '@shared/helpers/app';
import { pluralizeWord } from '@shared/helpers/formatters';
import { formatUsd } from '@shared/helpers/number';

import AppCategory from '@store:web/components/common/AppCard/AppCategory';
import AppHotOfferLink from '@store:web/components/common/AppCard/AppHotOfferLink';
import Tabs from '@store:web/components/common/Tabs/Tabs';
import AppTokenSwapButton from '@store:web/components/pages/AppPage/AppTokenSwapButton';
import { formatTokenPriceChange } from '@store:web/utils/helpers';
import { useAppInSpace } from '@store:web/utils/hooks/useAppInSpace';
import { useAppLike } from '@store:web/utils/hooks/useAppLike';
import useAppOpenLink, {
  transformDeserializedAppToOpenableApp,
} from '@store:web/utils/hooks/useAppLink';

import AppDetailsGallery from './AppDetailsGallery';
import AppDetailsInteractionStats from './AppDetailsInteractionStats';
import AppDetailsSharing from './AppDetailsSharing';
import AppDetailsStatList from './AppDetailsStats';
import AppDetailsStatus from './AppDetailsStatus';
import AppDetailsStatusBanner from './AppDetailsStatusBanner';
import { AppPageSectionProps } from './AppPage';

const getAppChainInfo = (chains: DeserializedAppChainData[]) => {
  if (chains.length === 0) {
    return;
  }

  if (chains.length === 1) {
    return {
      name: chains[0].attributes.shortName,
      icon: chains[0].attributes.icon,
    };
  }

  return {
    name: 'Multi-chain',
    icon: multiChain,
  };
};

const AppDetailsSection = ({ app, validation, token }: AppPageSectionProps) => {
  const {
    id,
    attributes: {
      appIcon,
      appName,
      category,
      subCategory,
      shortDescription,
      longDescription,
      tokenId,
      status,
      productionReady,
      likes,
      downloads,
      introduction = '',
      overview = '',
      benefits = '',
      getStarted = '',
    },
    chains,
    tags,
    developer,
    media,
    hotOffers,
  } = app;
  const { isAuth } = useAuth();
  const loginModal = useAuthModal();
  const appLike = useAppLike({
    appId: id,
    appLink: app.attributes.appLink,
  });
  const openableApp = useMemo(() => transformDeserializedAppToOpenableApp(app), [app]);
  const appLink = useAppOpenLink(openableApp);
  const appInSpace = useAppInSpace(openableApp);
  const appStatus = getAppStatus({ productionReady, status });
  const { data: appToken } = useTokenQuery(
    {
      tokenId: tokenId || '',
    },
    {
      enabled: !!tokenId,
    },
  );
  const appOfferId = hotOffers?.[hotOffers.length - 1].attributes.offerId;
  const totalLikes = likes - Number(appLike.initialValue) + Number(appLike.value);
  const totalDownloads = downloads - Number(appInSpace.initialValue) + Number(appInSpace.value);
  const chain = chains && getAppChainInfo(chains);
  const latestTokenPrice = appToken?.prices && appToken.prices[appToken.prices.length - 1];

  const handleOpenAppLink = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!isAuth) {
      loginModal.open();

      e.preventDefault();
    }
  };

  const appsCount = developer?.attributes?.appsCount;
  const hasIcon = Boolean(developer?.attributes?.icon);
  const hasLink = Boolean(developer?.attributes?.link);
  const hasTags = Boolean(tags?.length);
  const hasMedia = Boolean(media?.length);
  const hasDayChange = Boolean(appToken?.attributes.dayChange);
  const showTabs = Boolean(introduction || overview || benefits || getStarted);
  const showAdditionalInfo = Boolean(hasMedia || longDescription || showTabs);

  const tabsContent: {
    name: string;
    content: string;
  }[] = [
    {
      name: 'Introduction',
      content: introduction,
    },
    {
      name: 'Overview',
      content: overview,
    },
    {
      name: category === 'Games' ? 'Gameplay' : 'Key Benefits & Features',
      content: benefits,
    },
    {
      name: 'How To Get Started',
      content: getStarted,
    },
  ];

  return (
    <div
      data-onboarding="explore-app"
      className="sm:p-0 divide-y divide-primary-200 bg-neutral-50 sm:overflow-hidden sm:app-page-section-card"
    >
      {appStatus && appStatus !== APP_STATUS.VALIDATED && (
        <AppDetailsStatusBanner
          app={app}
          appStatus={appStatus}
          className="-mx-store-container-px mb-store-container-py sm:m-0"
        />
      )}
      <header className="pb-6 sm:p-app-page-section-card-p flex flex-col gap-4 !border-t-0">
        <div className="flex flex-col gap-4 justify-between md:grid md:grid-cols-[1fr_auto] md:grid-rows-[1fr_auto] md:items-start">
          <div className="flex gap-6 min-w-0 xl:row-span-full">
            <div className="flex flex-shrink-0 flex-col gap-2">
              <ImageProxy
                alt={`${app.attributes.appName} Icon`}
                src={appIcon}
                width={96}
                height={96}
                className="w-24 h-24 rounded-2xl"
                data-qa="info-icon"
              />

              {chain && (
                <span
                  className="max-md:hidden flex t-body2 justify-center items-center gap-1"
                  data-qa="info-chain-text"
                >
                  <ImageProxy
                    alt="App's chain"
                    width={20}
                    height={20}
                    className="w-5 h-5 rounded-[1.25rem]"
                    src={chain.icon}
                    resizeType="fit"
                    data-qa="info-chain-icon"
                  />
                  {chain.name}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-2.5 flex-1 min-w-0">
              <div className="flex items-baseline gap-5 justify-between">
                <h1 className="text-lg font-semibold break-words" data-qa="info-title">
                  {' '}
                  {appName}
                </h1>

                <AppDetailsStatus
                  app={app}
                  validation={validation}
                  className="max-sm:hidden md:hidden"
                  status={appStatus}
                />
              </div>

              <div className="text-sm flex items-center gap-3">
                <AppCategory Component="h5" category={category} />

                {subCategory && (
                  <>
                    <span className="max-md:hidden inline-block h-3 w-px bg-neutral-200" />
                    <h5 className="max-md:hidden" data-qa="info-subcategory">
                      {subCategory}
                    </h5>
                  </>
                )}
              </div>

              {appToken && (
                <>
                  <h5 className="max-md:hidden text-sm flex gap-2.5">
                    <span className="font-semibold" data-qa="info-token">
                      {appToken.attributes.symbol}
                    </span>

                    {latestTokenPrice && (
                      <span className="font-light" data-qa="info-token-price">
                        {formatUsd(latestTokenPrice.attributes?.price, {
                          maximumFractionDigits: 5,
                        })}
                      </span>
                    )}

                    {hasDayChange && (
                      <span
                        className={classNames(
                          'font-light',
                          appToken.attributes.dayChange < 0 ? 'text-red-600' : 'text-green-600',
                        )}
                        data-qa="info-token-percent"
                      >
                        {formatTokenPriceChange(appToken.attributes.dayChange)}
                        &nbsp;
                        <span className="leading-none align-text-top">
                          {appToken.attributes.dayChange < 0 ? '↓' : '↑'}
                        </span>
                      </span>
                    )}
                  </h5>

                  <AppTokenSwapButton token={token} className="max-md:hidden" />
                </>
              )}

              {shortDescription && (
                <h5 className="text-sm font-light break-words" data-qa="info-short-description">
                  {shortDescription}
                </h5>
              )}
            </div>
          </div>

          <div
            className={classNames(
              'flex justify-between gap-4 max-md:flex-wrap md:order-3 md:col-span-full xl:col-span-1 xl:flex',
              {
                'max-md:hidden': !appOfferId,
              },
            )}
          >
            {appOfferId && (
              <div className="max-xl:flex-shrink-0 max-xl:min-w-[1.5rem] max-xl:flex justify-center">
                <AppHotOfferLink
                  className="max-sm:text-xs max-sm:min-h-[1.75rem]"
                  offerId={appOfferId}
                />
              </div>
            )}

            {appStatus !== APP_STATUS.UNVALIDATED && (
              <AppDetailsInteractionStats
                className="max-md:hidden"
                downloads={totalDownloads}
                likes={totalLikes}
              />
            )}
            <AppDetailsStatus
              app={app}
              validation={validation}
              className="max-md:hidden"
              status={appStatus}
            />
          </div>

          <div className="flex flex-row md:flex-col gap-2 items-end">
            <div className="flex max-sm:flex-wrap gap-2 justify-end items-end xl:order-3">
              <div className="flex gap-4 items-center max-sm:flex-1 justify-between">
                <AppDetailsStatus
                  app={app}
                  validation={validation}
                  className="sm:hidden"
                  status={appStatus}
                />

                {appStatus !== APP_STATUS.UNVALIDATED && (
                  <div className="flex gap-2">
                    <AppDetailsSharing id={app.attributes.appLink} />
                    {isAuth && (
                      <button
                        className="icon-button button-ghost button-lg button-color-secondary"
                        onClick={appLike.toggle}
                        data-qa="info-like"
                      >
                        <span className="icon-sm">
                          {appLike.value ? <FilledHeart /> : <OutlinedHeart />}
                        </span>
                      </button>
                    )}
                  </div>
                )}
              </div>

              {appStatus !== APP_STATUS.UNVALIDATED && (
                <div className="flex max-sm:flex-1 gap-2">
                  <button
                    onClick={() => {
                      if (!isAuth) {
                        loginModal.open();
                      } else {
                        appInSpace.toggle();
                      }
                    }}
                    className={classNames(
                      'button button-lg min-w-[100px] flex-grow',
                      appInSpace.value ? 'button-solid' : 'button-outlined',
                    )}
                    data-qa="info-follow-button"
                  >
                    {appInSpace.value ? 'Unfollow' : 'Follow'}
                  </button>

                  <NextLink
                    {...(typeof appLink === 'string'
                      ? {
                          target: '_blank',
                          rel: 'noreferrer',
                          href: appLink,
                        }
                      : {
                          href: appLink.url,
                        })}
                    onClick={handleOpenAppLink}
                    className="button button-outlined button-lg max-sm:self-start"
                  >
                    Open
                  </NextLink>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <AppDetailsStatList app={app} />

      {showAdditionalInfo && (
        <div className="py-6 -mx-store-container-px px-store-container-px sm:mx-0 sm:p-app-page-section-card-p flex flex-col gap-7">
          {hasMedia && (
            <div className="-mx-store-container-px sm:-mx-app-page-section-card-p">
              <AppDetailsGallery
                appName={appName}
                media={media}
                className="px-store-container-px sm:px-app-page-section-card-p"
              />
            </div>
          )}

          {longDescription && (
            <>
              <h2 className="text-lg font-semibold break-words" data-qa="app-info-about">
                About {appName}
              </h2>
              <p className="text-sm" data-qa="info-long-description">
                <ReadMoreText text={longDescription} charLimit={500} />
              </p>
            </>
          )}

          {showTabs && <Tabs TabWrapper="h4" className="sm:mx-0 !border-t-0" tabs={tabsContent} />}
        </div>
      )}

      <div className="flex flex-wrap max-sm:!border-b gap-10 py-6 -mx-store-container-px px-store-container-px justify-between items-center sm:mx-0 sm:p-app-page-section-card-p">
        {hasTags && (
          <ul className="flex gap-2.5 flex-wrap">
            {tags?.slice(0, 10)?.map((tag) => {
              return (
                <li key={tag.id}>
                  <NextLink
                    className="h-6 px-3 inline-flex items-center border rounded-full border-primary-200 hover:border-primary hover:bg-primary-100 transition-colors t-caption2 shadow-card text-neutral-600"
                    href={`/search?${new URLSearchParams({ tags: tag.attributes.tag }).toString()}`}
                    data-qa="info-tag"
                  >
                    {tag.attributes.tag}
                  </NextLink>
                </li>
              );
            })}
          </ul>
        )}

        {hasLink && (
          <NextLink href={`/developer/${developer.attributes.link}`} className="flex gap-4">
            {hasIcon && (
              <ImageProxy
                alt={`${developer.attributes.name} Developer`}
                width={50}
                height={50}
                src={developer.attributes.icon}
                className="w-12 h-12 flex-shrink-0 rounded-full object-cover"
                data-qa="creator-avatar"
              />
            )}
            <div className="flex flex-col gap-2">
              <span className="t-h4" data-qa="creator-title">
                {developer.attributes.name}
              </span>
              <span
                className="flex items-center gap-2 t-caption1 font-light text-neutral-600"
                data-qa="creator-text"
              >
                <Loading className="icon text-secondary-700" />
                {`${appsCount} ${pluralizeWord('Project', appsCount)}`}
              </span>
            </div>
          </NextLink>
        )}
      </div>
    </div>
  );
};

export default AppDetailsSection;
